/**
 * Till API surface (M4).
 *
 * Money is a string, as everywhere else in this client: the API sends a Postgres NUMERIC
 * as text because it does not survive a double, and it becomes a number only inside
 * `formatMoney`. The till is the one screen where that matters twice over -- the customer
 * is standing there watching the figure, and the drawer has to balance at the end of the
 * shift against what these strings said.
 *
 * There is no cart on the server. The screen holds the lines and posts the whole sale
 * once, so an abandoned sale leaves nothing behind. What the screen prices is a quote:
 * every figure is re-derived server-side at checkout, and the response is what goes on the
 * receipt. See `cart.ts` for the quote and `CheckoutRequest` in the API for why.
 */

import { API_PREFIX, apiBlob, apiFetch, apiGet, type Page } from '@/api/client'

export const RESOURCE = 'pos'

// --- terminals and shifts --------------------------------------------------------------

export const TERMINAL_STATUSES = ['active', 'inactive', 'maintenance'] as const

export type TerminalStatus = (typeof TERMINAL_STATUSES)[number]

export interface Terminal {
  id: string
  code: string
  name: string
  location_id: string
  status: TerminalStatus
  notes: string | null
  /** The shift currently open on this till, if any. Null means it cannot sell yet. */
  open_shift_id: string | null
}

export interface Shift {
  id: string
  terminal_id: string
  terminal_name: string | null
  terminal_code: string | null
  location_id: string
  location_name: string | null
  opened_by: string | null
  opened_by_name: string | null
  opened_at: string
  opening_float: string
  closed_by: string | null
  closed_by_name: string | null
  closed_at: string | null
  closing_cash: string | null
  expected_cash: string | null
  variance: string | null
  status: 'open' | 'closed'
  notes: string | null
}

export interface ShiftSummary {
  shift_id: string
  status: string
  opening_float: string
  transaction_count: number
  voided_count: number
  gross_sales: string
  discount_amount: string
  tax_amount: string
  net_sales: string
  cash_takings: string
  card_takings: string
  store_credit_takings: string
  /** Float plus cash taken, less change given. What should be in the drawer. */
  expected_cash: string
  counted_cash: string | null
  variance: string | null
}

export const TERMINAL_STATUS_LABELS: Record<TerminalStatus, string> = {
  active: 'Active',
  inactive: 'Inactive',
  maintenance: 'Maintenance',
}

export const listTerminals = (locationId?: string) =>
  apiGet<Terminal[]>(
    `${API_PREFIX}/pos/terminals${locationId ? `?location_id=${locationId}` : ''}`,
  )

export const createTerminal = (input: {
  code: string
  name: string
  location_id: string
  status: TerminalStatus
  notes?: string | null
}) => apiGet<Terminal>(`${API_PREFIX}/pos/terminals`, { method: 'POST', body: JSON.stringify(input) })

export const updateTerminal = (
  id: string,
  input: { name?: string; status?: TerminalStatus; notes?: string | null },
) =>
  apiGet<Terminal>(`${API_PREFIX}/pos/terminals/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  })

export const listShifts = (
  params: { terminal_id?: string; location_id?: string; status?: string; page?: number } = {},
) => {
  const query = new URLSearchParams()
  if (params.terminal_id) query.set('terminal_id', params.terminal_id)
  if (params.location_id) query.set('location_id', params.location_id)
  if (params.status) query.set('status', params.status)
  if (params.page && params.page > 1) query.set('page', String(params.page))
  const qs = query.toString()
  return apiFetch<Page<Shift>>(`${API_PREFIX}/pos/shifts${qs ? `?${qs}` : ''}`)
}

export const openShift = (input: { terminal_id: string; opening_float: string; notes?: string }) =>
  apiGet<Shift>(`${API_PREFIX}/pos/shifts`, { method: 'POST', body: JSON.stringify(input) })

export const getShiftSummary = (shiftId: string) =>
  apiGet<ShiftSummary>(`${API_PREFIX}/pos/shifts/${shiftId}/summary`)

export const closeShift = (shiftId: string, input: { closing_cash: string; notes?: string }) =>
  apiGet<ShiftSummary>(`${API_PREFIX}/pos/shifts/${shiftId}/close`, {
    method: 'POST',
    body: JSON.stringify(input),
  })

// --- the catalog, as the till sees it ----------------------------------------------------

/**
 * Enough to scan, price and show. No cost: the screen is operated by store staff, and
 * margin is not something to leave on a counter-facing display.
 */
export interface TillProduct {
  id: string
  code: string
  name: string
  unit_of_measure: string
  price: string
  tax_rate: string
  tax_code: string | null
  category_id: string
  /** The product's own discount at this till's location, in percent. Zero when none. */
  promo_pct: string
  /** On hand at the till's location, advisory. The lock at checkout is what decides. */
  quantity: number | null
  /** Sent by the search only, to tell two similar names apart. */
  category_name?: string | null
}

/** A miss is a 404, not an empty envelope: the till plays a different sound for each. */
// The till says where it is so the server can settle a location-limited discount.
export const scanCode = (code: string, locationId?: string | null) =>
  apiGet<TillProduct>(
    `${API_PREFIX}/pos/catalog/scan/${encodeURIComponent(code)}${
      locationId ? `?location_id=${encodeURIComponent(locationId)}` : ''
    }`,
  )

export const searchCatalog = (q: string, limit = 20, locationId?: string | null) =>
  apiGet<TillProduct[]>(
    `${API_PREFIX}/pos/catalog/search?q=${encodeURIComponent(q)}&limit=${limit}${
      locationId ? `&location_id=${encodeURIComponent(locationId)}` : ''
    }`,
  )

// --- selling -----------------------------------------------------------------------------

export const TENDER_METHODS = ['cash', 'card', 'store_credit'] as const

export type TenderMethod = (typeof TENDER_METHODS)[number]

export const TENDER_LABELS: Record<TenderMethod, string> = {
  cash: 'Cash',
  card: 'Card',
  store_credit: 'Store credit',
}

export interface CheckoutItem {
  product_id: string
  quantity: number
  /** Money off this line, not a percentage — the receipt has to add up to the amount. */
  discount_amount: string
}

export interface CheckoutTender {
  method: TenderMethod
  /** What this tender settles. Cash handed over goes in `cash_tendered`. */
  amount: string
  reference?: string | null
}

export interface CheckoutRequest {
  terminal_id: string
  items: CheckoutItem[]
  payments: CheckoutTender[]
  customer_id?: string | null
  discount_amount?: string
  cash_tendered?: string
  notes?: string | null
}

export interface TransactionItem {
  id: string
  product_id: string
  product_data: { code?: string; name?: string; unit_of_measure?: string }
  quantity: number
  unit_price: string
  discount_amount: string
  taxable: string
  tax_rate: string
  tax_amount: string
  line_total: string
  tax_code: string | null
}

export interface TransactionPayment {
  id: string
  method: TenderMethod
  amount: string
  reference: string | null
}

export interface TaxBand {
  rate: string
  taxable: string
  tax: string
  tax_code?: string | null
}

export interface Sale {
  id: string
  receipt_number: string
  terminal_id: string
  shift_id: string
  location_id: string
  customer_id: string | null
  cashier_id: string | null
  subtotal: string
  discount_amount: string
  tax_amount: string
  round_off: string
  total: string
  /**
   * The tax split frozen on the sale, named for its shape rather than its country: two
   * authorities fill primary and secondary (India's CGST + SGST), one fills unified
   * (IGST, and every US sale). Which labels apply is `RegimeInfo.component_labels`' to
   * say — never the field name.
   */
  primary_tax_amount: string
  secondary_tax_amount: string
  unified_tax_amount: string
  tax_regime: string
  supply_type: string
  /** The buyer as the receipt prints them, frozen at the sale. Empty for a walk-in. */
  customer_data: Record<string, string>
  /** Where the supply happened, which is what decides the split. */
  place_of_supply_code: string | null
  place_of_supply: string | null
  tax_breakdown: TaxBand[]
  cash_tendered: string
  change_given: string
  status: 'completed' | 'voided'
  completed_at: string
  business_date: string
  voided_at: string | null
  void_reason: string | null
  notes: string | null
  items: TransactionItem[]
  payments: TransactionPayment[]
}

/**
 * Today at the counter, as it stands this second.
 *
 * Deliberately not an analytics call. The rollups answer "the last thirty days" and are a
 * dispatch behind; this is summed off the sales themselves, which is the only place "so
 * far today" exists. Cheap enough to poll between customers.
 */
export interface CounterDay {
  business_date: string
  location_id: string | null
  transaction_count: number
  voided_count: number
  gross_sales: string
  discount_amount: string
  tax_amount: string
  net_sales: string
  cash_takings: string
  card_takings: string
  store_credit_takings: string
  average_basket: string
  /** Null until the first sale of the day. What tells a quiet screen from a broken one. */
  last_sale_at: string | null
}

export const getCounterDay = (locationId?: string) =>
  apiGet<CounterDay>(
    `${API_PREFIX}/pos/today${locationId ? `?location_id=${locationId}` : ''}`,
  )

/**
 * Ring up a sale. The `Idempotency-Key` is not optional, here least of all.
 *
 * A cashier whose network drops between the request and the response has no way to know
 * whether the customer was charged, and the only thing they can do is press the button
 * again. The key is minted once per submission by the caller and reused across every
 * retry of that same submission — generating it in here would defeat the point, because
 * each retry would carry a fresh key and the server would ring up a second sale.
 */
export const checkout = (input: CheckoutRequest, idempotencyKey: string) =>
  apiGet<Sale>(`${API_PREFIX}/pos/checkout`, {
    method: 'POST',
    body: JSON.stringify(input),
    headers: { 'Idempotency-Key': idempotencyKey },
  })

export const getSale = (id: string) => apiGet<Sale>(`${API_PREFIX}/pos/transactions/${id}`)

export const listSales = (
  params: {
    shift_id?: string
    terminal_id?: string
    customer_id?: string
    location_id?: string
    status?: string
    q?: string
    date_from?: string
    date_to?: string
    page?: number
    page_size?: number
  } = {},
) => {
  const query = new URLSearchParams()
  if (params.shift_id) query.set('shift_id', params.shift_id)
  if (params.terminal_id) query.set('terminal_id', params.terminal_id)
  if (params.customer_id) query.set('customer_id', params.customer_id)
  if (params.location_id) query.set('location_id', params.location_id)
  if (params.status) query.set('status', params.status)
  if (params.q) query.set('q', params.q)
  if (params.date_from) query.set('date_from', params.date_from)
  if (params.date_to) query.set('date_to', params.date_to)
  if (params.page && params.page > 1) query.set('page', String(params.page))
  if (params.page_size) query.set('page_size', String(params.page_size))
  const qs = query.toString()
  return apiFetch<Page<Sale>>(`${API_PREFIX}/pos/transactions${qs ? `?${qs}` : ''}`)
}

export const voidSale = (id: string, reason: string) =>
  apiGet<Sale>(`${API_PREFIX}/pos/transactions/${id}/void`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  })

// --- receipts -----------------------------------------------------------------------------

/**
 * The receipt as a PDF, fetched rather than linked: the route needs the bearer token and
 * an `<a href>` cannot carry one. The caller revokes the blob URL it builds from this.
 */
export const fetchReceiptPdf = (id: string) =>
  apiBlob(`${API_PREFIX}/pos/transactions/${id}/receipt`)

/**
 * The same receipt as a page the browser can print.
 *
 * Fetched as text and printed from a hidden iframe rather than opened in a tab: a popup
 * blocker between the cashier and the customer's receipt is not a failure mode worth
 * having, and the server's stylesheet already sizes the paper.
 */
export const fetchReceiptHtml = (id: string) =>
  apiBlob(`${API_PREFIX}/pos/transactions/${id}/receipt/print`).then((blob) => blob.text())

export interface ReceiptSent {
  receipt_number: string
  email: string
}

/** Omit the address to use the one on the customer's account. */
export const emailReceipt = (id: string, email?: string) =>
  apiGet<ReceiptSent>(`${API_PREFIX}/pos/transactions/${id}/receipt/email`, {
    method: 'POST',
    body: JSON.stringify(email ? { email } : {}),
  })

// --- store credit ---------------------------------------------------------------------

export interface StoreCreditEntry {
  id: string
  customer_id: string
  amount: string
  reason: string
  transaction_id: string | null
  notes: string | null
  created_at: string
}

export interface StoreCreditBalance {
  customer_id: string
  balance: string
  entries: StoreCreditEntry[]
}

export const getStoreCredit = (customerId: string) =>
  apiGet<StoreCreditBalance>(`${API_PREFIX}/pos/store-credit/${customerId}`)

// --- odds and ends ----------------------------------------------------------------------

/** A key that survives re-renders is the caller's job; this only makes one. */
export const newIdempotencyKey = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`

/**
 * The till this browser is. Remembered because a terminal is a physical machine on a
 * counter, not a preference of whoever is signed in: the next cashier takes over the same
 * drawer, and being asked which till they are standing at is a question the browser can
 * already answer.
 */
const TERMINAL_KEY = 'fmcg.pos.terminal'

export function rememberedTerminal(): string | null {
  try {
    return localStorage.getItem(TERMINAL_KEY)
  } catch {
    return null
  }
}

export function rememberTerminal(terminalId: string | null): void {
  try {
    if (terminalId) localStorage.setItem(TERMINAL_KEY, terminalId)
    else localStorage.removeItem(TERMINAL_KEY)
  } catch {
    /* private mode, or storage disabled: the picker just asks again next time */
  }
}

export { formatMoney } from '@/lib/money'
